import React, { useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DragHandleDots2Icon, PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { LIBRARY_TAB_RC_CONFIG } from './rcConfig'
import {
  COUNTRY_PRESETS,
  DEFAULT_SCOPE,
  DEFAULT_SCOPE_LABEL,
  PLATFORMS,
  PRODUCTS,
  RC_PARAMETER_KEY,
  createEmptyItem,
  defaultConditionName,
  isDefaultScope,
  isFixedScope,
  matrixScopes,
  normalizeCountryCode,
  scopeLabel,
} from './config'
import {
  buildRcPreviews,
  formatAllRcJson,
  formatRcJson,
} from './buildRcPreview'
import {
  fetchLibraryTabConf,
  publishLibraryTabConf,
  type AvailableCondition,
} from './api'
import type {
  CountryCode,
  LibraryTabDraft,
  LibraryTabItem,
  PlatformId,
  ProductId,
  RcConditionPreview,
} from './types'

type PendingDelete =
  | { type: 'tab'; item: LibraryTabItem }
  | { type: 'country'; country: CountryCode }

function ConfirmDeleteDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: PendingDelete | null
  onCancel: () => void
  onConfirm: () => void
}) {
  if (!pending) return null

  const isTab = pending.type === 'tab'
  const tabLabel =
    isTab &&
    (pending.item.tag?.trim() ||
      Object.values(pending.item.titles).find((t) => t?.trim()) ||
      pending.item.id)
  const countryLabel = !isTab ? scopeLabel(pending.country) : ''

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isTab ? '确认删除分栏' : '确认删除国家'}</DialogTitle>
          <DialogDescription>
            {isTab
              ? `确定删除分栏「${tabLabel}」吗？删除后该分栏会从所有国家配置中移除。`
              : `确定删除国家「${countryLabel}」吗？删除后将移除该国家列及其标题、可见性配置。`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            取消
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm}>
            确认删除
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SortableRow({
  item,
  countries,
  onChange,
  onRemove,
}: {
  item: LibraryTabItem
  countries: CountryCode[]
  onChange: (next: LibraryTabItem) => void
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const toggleVisible = (country: CountryCode, checked: boolean) => {
    const visibleIn = checked
      ? Array.from(new Set([...item.visibleIn, country]))
      : item.visibleIn.filter((c) => c !== country)
    onChange({ ...item, visibleIn })
  }

  const setTitle = (country: CountryCode, title: string) => {
    onChange({
      ...item,
      titles: { ...item.titles, [country]: title },
    })
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-b bg-background',
        isDragging && 'opacity-60 shadow-md z-10 relative',
      )}
    >
      <td className="sticky left-0 z-20 bg-background px-2 py-2 w-10">
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded border text-muted-foreground cursor-grab active:cursor-grabbing"
          title="Drag to reorder (global)"
          {...attributes}
          {...listeners}
        >
          <DragHandleDots2Icon className="h-4 w-4" />
        </button>
      </td>
      <td className="sticky left-10 z-20 bg-background px-2 py-2 min-w-[140px]">
        <Input
          value={item.tag}
          placeholder="tag / key"
          className="h-8 font-mono text-xs"
          onChange={(e) => onChange({ ...item, tag: e.target.value })}
        />
      </td>
      <td className="sticky left-[180px] z-20 bg-background px-2 py-2 min-w-[100px]">
        <Input
          value={item.type ?? ''}
          placeholder="optional"
          className="h-8 font-mono text-xs"
          onChange={(e) =>
            onChange({ ...item, type: e.target.value || undefined })
          }
        />
      </td>
      {countries.map((country) => {
        const visible = item.visibleIn.includes(country)
        const title = item.titles[country] ?? ''
        const missing = visible && !title.trim()
        return (
          <td key={country} className="px-2 py-2 min-w-[180px] align-middle">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={visible}
                title="Show in this country"
                aria-label={`Show in ${country}`}
                onCheckedChange={(v) => toggleVisible(country, v === true)}
              />
              <Input
                value={title}
                disabled={!visible}
                placeholder={visible ? 'title' : '—'}
                className={cn(
                  'h-8 text-xs flex-1 min-w-0',
                  missing && 'border-destructive focus-visible:ring-destructive',
                  !visible && 'opacity-40',
                )}
                onChange={(e) => setTitle(country, e.target.value)}
              />
            </div>
          </td>
        )
      })}
      <td className="px-2 py-2 w-12">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          title="Remove tab"
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  )
}

function IndependentOrderDialog({
  open,
  onOpenChange,
  country,
  draft,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  country: CountryCode | null
  draft: LibraryTabDraft
  onSave: (country: CountryCode, order: string[] | null) => void
}) {
  const [ids, setIds] = useState<string[]>([])

  useEffect(() => {
    if (!open || !country) return
    const visible = draft.items.filter((i) => i.visibleIn.includes(country))
    const state = draft.countryOrders[country]
    if (state?.mode === 'independent') {
      const byId = new Map(visible.map((i) => [i.id, i]))
      const next: string[] = []
      for (const id of state.order) {
        if (byId.has(id)) {
          next.push(id)
          byId.delete(id)
        }
      }
      for (const item of byId.values()) next.push(item.id)
      setIds(next)
    } else {
      setIds(visible.map((i) => i.id))
    }
  }, [open, country, draft])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  if (!country) return null

  const labelById = new Map(
    draft.items.map((i) => [
      i.id,
      `${i.tag || '(empty tag)'} · ${i.titles[country] || '(no title)'}`,
    ]),
  )

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setIds((prev) => {
      const oldIndex = prev.indexOf(String(active.id))
      const newIndex = prev.indexOf(String(over.id))
      if (oldIndex < 0 || newIndex < 0) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Independent order · {scopeLabel(country)}</DialogTitle>
          <DialogDescription>
            Drag to set order for this country only. Global reorder will no
            longer affect it until reset.
          </DialogDescription>
        </DialogHeader>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2 max-h-[50vh] overflow-auto">
              {ids.map((id) => (
                <IndependentOrderItem
                  key={id}
                  id={id}
                  label={labelById.get(id) ?? id}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onSave(country, null)
              onOpenChange(false)
            }}
          >
            Follow global
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSave(country, ids)
              onOpenChange(false)
            }}
          >
            Save independent order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function IndependentOrderItem({ id, label }: { id: string; label: string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        'flex items-center gap-2 rounded border px-2 py-2 text-sm',
        isDragging && 'opacity-60',
      )}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground"
        {...attributes}
        {...listeners}
      >
        <DragHandleDots2Icon className="h-4 w-4" />
      </button>
      <span className="truncate">{label}</span>
    </li>
  )
}

function PreviewRcDialog({
  open,
  onOpenChange,
  previews,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  previews: RcConditionPreview[]
}) {
  const { toast } = useToast()
  const [active, setActive] = useState<string>('')

  useEffect(() => {
    if (open && previews.length) {
      setActive(previews[0].condition)
    }
  }, [open, previews])

  const current = previews.find((p) => p.condition === active) ?? previews[0]
  const allJson = useMemo(() => formatAllRcJson(previews), [previews])

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: 'Copied' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Copy failed',
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Preview RC JSON · library_tab_conf</DialogTitle>
          <DialogDescription>
            Preview of what will be written to{' '}
            <code>{RC_PARAMETER_KEY}</code>. iOS uses <code>key</code>, Android
            uses <code>tag</code>. Click Publish to push to Firebase.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5">
          {previews.map((p) => (
            <Button
              key={p.condition}
              type="button"
              size="sm"
              variant={p.condition === current?.condition ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setActive(p.condition)}
            >
              {p.isDefault
                ? `${DEFAULT_SCOPE_LABEL} · ${p.condition}`
                : p.condition}
              {p.warnings.length > 0 && (
                <span className="ml-1 text-amber-500">!</span>
              )}
            </Button>
          ))}
        </div>

        {current && (
          <div className="space-y-2 min-h-0 flex-1 flex flex-col">
            {current.warnings.length > 0 && (
              <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                {current.warnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </div>
            )}
            <pre className="flex-1 overflow-auto rounded-md border bg-muted/40 p-3 text-xs font-mono whitespace-pre">
              {formatRcJson(current.entries)}
            </pre>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => copy(allJson)}
          >
            Copy all conditions
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                current && copy(formatRcJson(current.entries))
              }
            >
              Copy current
            </Button>
            <Button type="button" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


function AddCountryDialog({
  open,
  onOpenChange,
  draft,
  availableConditions,
  isVersion,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  draft: LibraryTabDraft
  availableConditions: AvailableCondition[]
  isVersion: boolean
  onAdd: (country: CountryCode, conditionName: string) => void
}) {
  const [code, setCode] = useState('')
  const [conditionName, setConditionName] = useState('')
  const existing = new Set(draft.countries.map((c) => c.toUpperCase()))
  const unusedConditions = availableConditions.filter(
    (c) =>
      c.scope !== DEFAULT_SCOPE &&
      !existing.has(c.scope.toUpperCase()) &&
      !draft.countries.includes(c.scope),
  )
  const presets = [
    ...unusedConditions.map((c) => c.scope),
    ...COUNTRY_PRESETS.filter(
      (c) =>
        !existing.has(c.toUpperCase()) &&
        !unusedConditions.some((u) => u.scope.toUpperCase() === c.toUpperCase()),
    ),
  ]

  useEffect(() => {
    if (!open) return
    setCode('')
    setConditionName('')
  }, [open])

  useEffect(() => {
    const normalized = normalizeCountryCode(code)
    if (!normalized) {
      setConditionName('')
      return
    }
    setConditionName(
      defaultConditionName(
        draft.product,
        draft.platform,
        normalized,
        isVersion,
      ),
    )
  }, [code, draft.product, draft.platform, isVersion])

  const submit = () => {
    const normalized = normalizeCountryCode(code)
    if (!normalized) return
    if (normalized === DEFAULT_SCOPE || isDefaultScope(normalized)) return
    if (existing.has(normalized.toUpperCase())) return
    onAdd(
      normalized,
      conditionName.trim() ||
        defaultConditionName(
          draft.product,
          draft.platform,
          normalized,
          isVersion,
        ),
    )
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add country</DialogTitle>
          <DialogDescription>
            Adds a column mapped to{' '}
            <code>{`Neku_Android_US${LIBRARY_TAB_RC_CONFIG.conditionSuffix}${isVersion ? '_Version' : ''}`}</code>
            . {isVersion
              ? '当前写入 *_Version 条件，版本规则在 Firebase 条件里配。'
              : `未勾 isVersion 时，条件必须已经挂在 ${LIBRARY_TAB_RC_CONFIG.parameterKey} 上，否则发布失败 — 联系潇娜添加。`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Country code</Label>
            <Input
              value={code}
              placeholder="e.g. ID / VN / HK-TW"
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
          </div>

          {presets.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {presets.slice(0, 16).map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => {
                    setCode(preset)
                    const hit = availableConditions.find(
                      (c) => c.scope.toUpperCase() === preset.toUpperCase(),
                    )
                    if (hit) setConditionName(hit.name)
                  }}
                >
                  {scopeLabel(preset)}
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>RC condition name</Label>
            <Input
              value={conditionName}
              placeholder="Neku_Android_ID"
              className="font-mono text-xs"
              onChange={(e) => setConditionName(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!normalizeCountryCode(code) || existing.has(normalizeCountryCode(code).toUpperCase())}
            onClick={submit}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function LibraryTabConfTab() {
  const { toast } = useToast()
  const [product, setProduct] = useState<ProductId>('neku')
  const [platform, setPlatform] = useState<PlatformId>('android')
  const [isVersion, setIsVersion] = useState(false)
  const [clonedFromUnversioned, setClonedFromUnversioned] = useState(false)
  const [publishConfirm, setPublishConfirm] = useState<
    'versioned' | 'unversioned' | null
  >(null)
  const [draft, setDraft] = useState<LibraryTabDraft>({
    product: 'neku',
    platform: 'android',
    countries: [],
    conditionNames: {},
    items: [],
    countryOrders: {},
  })
  const [etag, setEtag] = useState('')
  const [availableConditions, setAvailableConditions] = useState<
    AvailableCondition[]
  >([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [addCountryOpen, setAddCountryOpen] = useState(false)
  const [orderCountry, setOrderCountry] = useState<CountryCode | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  )

  const countries = matrixScopes(draft.countries ?? [])

  const loadFromFirebase = async (
    nextPlatform: PlatformId,
    nextIsVersion = isVersion,
  ) => {
    setLoading(true)
    try {
      const result = await fetchLibraryTabConf(nextPlatform, nextIsVersion)
      setDraft(result.draft)
      setEtag(result.etag)
      setAvailableConditions(result.availableConditions ?? [])
      setClonedFromUnversioned(Boolean(result.clonedFromUnversioned))
      setDirty(false)
      toast({
        title: 'Loaded from Firebase',
        description: nextIsVersion
          ? `${result.parameterKey} · ${nextPlatform} · *_Version` +
            (result.clonedFromUnversioned ? '（从旧配置复制）' : '')
          : `${result.parameterKey} · ${nextPlatform} · 旧条件`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: 'Load failed',
        description: message,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadFromFirebase(platform, isVersion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, isVersion])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const previews = useMemo(() => buildRcPreviews(draft), [draft])
  const warningCount = previews.reduce((n, p) => n + p.warnings.length, 0)
  const independentCountries = countries.filter(
    (c) => draft.countryOrders[c]?.mode === 'independent',
  )

  const updateDraft = (
    updater: (prev: LibraryTabDraft) => LibraryTabDraft,
  ) => {
    setDraft((prev) => updater(prev))
    setDirty(true)
  }

  const updateItems = (items: LibraryTabItem[]) => {
    updateDraft((prev) => ({ ...prev, items }))
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    updateDraft((prev) => {
      const oldIndex = prev.items.findIndex((i) => i.id === active.id)
      const newIndex = prev.items.findIndex((i) => i.id === over.id)
      if (oldIndex < 0 || newIndex < 0) return prev
      return { ...prev, items: arrayMove(prev.items, oldIndex, newIndex) }
    })
  }

  const onSaveIndependent = (country: CountryCode, order: string[] | null) => {
    updateDraft((prev) => {
      const countryOrders = { ...prev.countryOrders }
      if (order === null) {
        delete countryOrders[country]
      } else {
        countryOrders[country] = { mode: 'independent', order }
      }
      return { ...prev, countryOrders }
    })
    toast({
      title:
        order === null
          ? `Reset ${scopeLabel(country)} to global order`
          : `${scopeLabel(country)} uses independent order`,
    })
  }

  const onPublishClick = () => {
    if (!etag) {
      toast({ variant: 'destructive', title: 'Missing etag, please reload' })
      return
    }
    if (warningCount > 0) {
      toast({
        variant: 'destructive',
        title: 'Fix warnings before publish',
        description: `${warningCount} issue(s) in preview`,
      })
      return
    }
    setPublishConfirm(isVersion ? 'versioned' : 'unversioned')
  }

  const onPublish = async () => {
    setPublishConfirm(null)
    setSaving(true)
    try {
      const result = await publishLibraryTabConf({
        platform,
        etag,
        draft,
        isVersion,
      })
      setEtag(result.etag)
      setClonedFromUnversioned(false)
      setDirty(false)
      toast({
        title: 'Published to Firebase',
        description: isVersion
          ? `${result.parameterKey} · ${platform} · *_Version · 旧条件未改`
          : `${result.parameterKey} · ${platform} · 已改旧条件`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: 'Publish failed',
        description: message,
      })
    } finally {
      setSaving(false)
    }
  }

  const onAddCountry = (country: CountryCode, conditionName: string) => {
    if (isFixedScope(country)) {
      toast({
        variant: 'destructive',
        title: '通用 column is always present',
      })
      return
    }
    updateDraft((prev) => {
      if (prev.countries.some((c) => c.toUpperCase() === country.toUpperCase())) {
        return prev
      }
      return {
        ...prev,
        countries: [...prev.countries, country],
        conditionNames: {
          ...prev.conditionNames,
          [country]: conditionName,
        },
      }
    })
    toast({ title: `Added country ${country}` })
  }

  const removeCountry = (country: CountryCode) => {
    if (isFixedScope(country)) {
      toast({
        variant: 'destructive',
        title: 'Cannot remove 通用',
      })
      return
    }
    updateDraft((prev) => {
      const countryOrders = { ...prev.countryOrders }
      delete countryOrders[country]
      const conditionNames = { ...prev.conditionNames }
      delete conditionNames[country]
      return {
        ...prev,
        countries: prev.countries.filter((c) => c !== country),
        conditionNames,
        countryOrders,
        items: prev.items.map((item) => {
          const { [country]: _removed, ...titles } = item.titles
          return {
            ...item,
            titles,
            visibleIn: item.visibleIn.filter((c) => c !== country),
          }
        }),
      }
    })
    if (orderCountry === country) setOrderCountry(null)
    toast({ title: `Removed country ${country}` })
  }

  const removeTab = (itemId: string) => {
    updateDraft((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.id !== itemId),
    }))
  }

  const confirmPendingDelete = () => {
    if (!pendingDelete) return
    if (pendingDelete.type === 'tab') {
      removeTab(pendingDelete.item.id)
    } else {
      removeCountry(pendingDelete.country)
    }
    setPendingDelete(null)
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          编辑 Firebase Remote Config 参数{' '}
          <code className="text-xs">{LIBRARY_TAB_RC_CONFIG.parameterKey}</code>
          。选 isVersion 后编辑{' '}
          <code className="text-xs">Neku_Android_US_Version</code> /{' '}
          <code className="text-xs">Neku_IOS_DE_Version</code>
          ，旧的{' '}
          <code className="text-xs">Neku_Android</code> /{' '}
          <code className="text-xs">Neku_IOS</code>{' '}
          不动。最低版本在 Firebase 条件里配，这里不用填。
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Select
              value={product}
              onValueChange={(v) => setProduct(v as ProductId)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Platform</Label>
            <Select
              value={platform}
              onValueChange={(v) => setPlatform(v as PlatformId)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>isVersion</Label>
            <RadioGroup
              value={isVersion ? 'true' : 'false'}
              onValueChange={(v) => setIsVersion(v === 'true')}
              className="flex h-10 flex-row items-center gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="false" id="is-version-off" />
                <Label htmlFor="is-version-off" className="font-normal">
                  否（旧条件）
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="true" id="is-version-on" />
                <Label htmlFor="is-version-on" className="font-normal">
                  是（*_Version）
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex flex-wrap gap-2 ml-auto items-center">
            {dirty && (
              <Badge variant="secondary">Unsaved</Badge>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={loading || saving}
              onClick={() => void loadFromFirebase(platform, isVersion)}
            >
              {loading ? 'Loading…' : 'Reload from Firebase'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPreviewOpen(true)}
            >
              Preview RC JSON
              {warningCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {warningCount} warn
                </Badge>
              )}
            </Button>
            <Button
              type="button"
              disabled={loading || saving || !etag}
              onClick={() => void onPublishClick()}
            >
              {saving ? 'Publishing…' : 'Publish to Firebase'}
            </Button>
          </div>
        </div>

        {isVersion ? (
          <p className="text-xs text-muted-foreground">
            当前编辑 *_Version 条件（
            {clonedFromUnversioned
              ? '该车道还没有值，表格从旧配置复制；发布前请确认潇娜已挂上对应 Version 条件'
              : '已有 Version 条件的值'}
            ）。旧版 Neku_Android / Neku_IOS 不会被这次发布改掉。
          </p>
        ) : (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            isVersion = 否：发布会改现有旧条件，所有版本用户都会看到。
          </p>
        )}

        {independentCountries.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Independent order:{' '}
            {independentCountries.map((c) => (
              <Badge key={c} variant="outline" className="mr-1">
                {scopeLabel(c)}
              </Badge>
            ))}
          </p>
        )}
      </div>

      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={draft.items.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="sticky left-0 z-30 bg-muted/50 px-2 py-2 w-10" />
                    <th className="sticky left-10 z-30 bg-muted/50 px-2 py-2 text-left font-medium min-w-[140px]">
                      Tag{' '}
                      <span className="text-xs font-normal text-muted-foreground">
                        ({platform === 'ios' ? '→ key' : '→ tag'})
                      </span>
                    </th>
                    <th className="sticky left-[180px] z-30 bg-muted/50 px-2 py-2 text-left font-medium min-w-[100px]">
                      Type{' '}
                      <span className="text-xs font-normal text-muted-foreground">
                        (optional)
                      </span>
                    </th>
                    {countries.map((country) => {
                      const independent =
                        draft.countryOrders[country]?.mode === 'independent'
                      return (
                        <th
                          key={country}
                          className="px-2 py-2 text-left font-medium min-w-[180px]"
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="truncate">
                              {scopeLabel(country)}
                              {isDefaultScope(country) && (
                                <Badge
                                  variant="outline"
                                  className="ml-1 text-[10px]"
                                >
                                  default
                                </Badge>
                              )}
                              {independent && (
                                <Badge
                                  variant="secondary"
                                  className="ml-1 text-[10px]"
                                >
                                  custom
                                </Badge>
                              )}
                            </span>
                            <div className="flex items-center shrink-0">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => setOrderCountry(country)}
                              >
                                Order
                              </Button>
                              {!isFixedScope(country) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  title={`Remove ${country}`}
                                  onClick={() =>
                                    setPendingDelete({ type: 'country', country })
                                  }
                                >
                                  <TrashIcon className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </th>
                      )
                    })}
                    <th className="px-2 py-2 w-12" />
                  </tr>
                </thead>
                <tbody>
                  {draft.items.map((item) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      countries={countries}
                      onChange={(next) =>
                        updateItems(
                          draft.items.map((i) =>
                            i.id === next.id ? next : i,
                          ),
                        )
                      }
                      onRemove={() =>
                        setPendingDelete({ type: 'tab', item })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>

        <div className="border-t p-3 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              updateItems([...draft.items, createEmptyItem(countries)])
            }
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            Add tab
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddCountryOpen(true)}
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            Add country
          </Button>
        </div>
      </div>

      <AddCountryDialog
        open={addCountryOpen}
        onOpenChange={setAddCountryOpen}
        draft={draft}
        availableConditions={availableConditions}
        isVersion={isVersion}
        onAdd={onAddCountry}
      />

      <IndependentOrderDialog
        open={orderCountry !== null}
        onOpenChange={(open) => {
          if (!open) setOrderCountry(null)
        }}
        country={orderCountry}
        draft={draft}
        onSave={onSaveIndependent}
      />

      <PreviewRcDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        previews={previews}
      />

      <ConfirmDeleteDialog
        pending={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmPendingDelete}
      />

      <Dialog
        open={publishConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setPublishConfirm(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {publishConfirm === 'versioned'
                ? '发布到 *_Version 条件'
                : '发布到旧条件'}
            </DialogTitle>
            <DialogDescription>
              {publishConfirm === 'versioned'
                ? `只会写入 Neku_${platform === 'ios' ? 'IOS' : 'Android'}_*_Version。现有 Neku_Android / Neku_IOS 旧条件不动。最低版本由 Firebase 条件控制。`
                : 'isVersion = 否，将直接改现有 Neku_Android / Neku_IOS 条件，所有版本（含老用户）都会看到。若只想给新包看，请先选 isVersion = 是。'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPublishConfirm(null)}
            >
              取消
            </Button>
            <Button type="button" onClick={() => void onPublish()}>
              确认发布
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
